import React, {
  useEffect, useState, useRef, useContext,
} from 'react'
import PropTypes from 'prop-types'
import _isUndefined from 'lodash/isUndefined'
import _isNil from 'lodash/isNil'
import { Map } from 'immutable'
import { connect as connectRedux, useStore } from 'react-redux'
import compose from 'recompose/compose'
import { getBoundActions } from '../../actions/index'
import { connect as connectStaticText } from '../StaticText/connect'
import LiveAccessSchedule from '../LiveAccessSchedule/LiveAccessSchedule'
import LiveVideoPlayer from '../LiveVideoPlayer/index'
import { scheduleKey, captionsStoreKey } from '../../services/live-access-events/reducers'
import { getSessionTimeoutLimited } from '../../services/live-access-events/index'
import { URL_GAIA_PLUS } from '../../services/url/constants'
import HistoryContext from '../Router/HistoryContext'
import { LiveAccessCaptionsIframe } from '../LiveAccessCaptions/LiveAccessCaptions'
import LiveExperienceControlBar from './LiveExperienceControlBar'

const LiveExperienceVideo = (props) => {
  const {
    staticText, language,
    getLiveAccessSessionDetails, timenowOverride, reloadEvent,
    eventDetails, setLiveAccessScheduleVisible, scheduleVisible, captionsVisible, captionsUrl,
    poster, debug, sessionDetails, trackTitles, getLiveAccessTrack, liveStreamActive, auth,
  } = props

  // why not just use a single state object vs. 4 useState hooks ???
  const [nowTs, setNowTs] = useState(timenowOverride ? Date.parse(timenowOverride) : Date.now())
  const [lastTimer, setLastTimer] = useState(0)
  const [initialNowTs] = useState(Date.now())
  const [track, setTrack] = useState(0)

  // Guard against null/undefined eventDetails
  if (!eventDetails || !Map.isMap(eventDetails)) {
    return null
  }

  const schedule = eventDetails.getIn(['tracks', track, 'schedule'])
  const sessionId = schedule && schedule.getIn([0, 'id'])
  const trackId = eventDetails.getIn(['tracks', 0, 'id'])
  const livePlaybackUrl = sessionDetails && sessionDetails.getIn([sessionId,
    language, 'data', 'session', 'playbackUrl'])
  const eventType = eventDetails.get('type')
  // Get track title for analytics (use track title instead of index)
  // trackTitles is an Immutable List, so use .get() method
  let trackTitle = `Track ${track}`
  if (trackTitles) {
    if (typeof trackTitles.get === 'function') {
      trackTitle = trackTitles.get(track) || trackTitle
    } else if (trackTitles[track]) {
      trackTitle = trackTitles[track]
    }
  }
  // Title must be "LIVE - route-name" for LIVE and AMA (stable live title for analytics).
  const eventRoute = eventDetails.get('route', '')
  const eventTitle = `LIVE - ${eventRoute}`
  // Match VideoPlayer pattern: always provide analyticsMeta with all required fields
  // LIVE/AMA streams don't have contentId or mediaId (those are VOD concepts)
  const viewerId = props.user.get('uid') || auth.get('uid')
  const isLoggedIn = Map.isMap(auth) && !!auth.get('jwt')
  // Always create analyticsMeta with all required fields (matching VideoPlayer PropTypes)
  // contentId: empty string for LIVE/AMA events (player won't use it for live streams anyway)
  // Use plain object (not Map) for PropTypes.shape() validation
  const analyticsMeta = {
    contentId: '', // Empty string - LIVE/AMA streams don't have contentId
    viewerId: viewerId ? String(viewerId) : '', // Empty string if no viewerId
    loggedIn: Boolean(isLoggedIn),
  }

  const history = useContext(HistoryContext)
  const timeoutRef = useRef()
  const store = useStore()

  const getTimeNow = () => {
    return timenowOverride
      ? Date.parse(timenowOverride) + (Date.now() - initialNowTs) : Date.now()
  }

  useEffect(() => {
    getLiveAccessSessionDetails({ id: sessionId, language })
  }, [sessionId])

  useEffect(() => {
    if (!_isUndefined(liveStreamActive) && !liveStreamActive) {
      reloadEvent()
    }
  }, [liveStreamActive])

  useEffect(() => {
    clearTimeout(timeoutRef.current)
    const tempNowTs = getTimeNow()
    setNowTs(tempNowTs)
    const timeoutDurationMS = schedule
      && getSessionTimeoutLimited(schedule.toJS(), tempNowTs) + 1000
    if (timeoutDurationMS) {
      timeoutRef.current = setTimeout(() => {
        setNowTs(getTimeNow())
        setLastTimer(Date.now())
      }, timeoutDurationMS)
    }
    getLiveAccessTrack({ id: trackId, language })
  }, [schedule, lastTimer])

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  // analyticsMeta is always valid - contentId is optional for LIVE/AMA events

  return (
    <div className="live-experience-video">
      <div className="live-experience-player-and-schedule">
        <div className="live-experience-player">
          {debug && livePlaybackUrl}
          {livePlaybackUrl
            && <LiveVideoPlayer
              title={eventTitle}
              playerSrc={livePlaybackUrl}
              posterOverride={poster}
              location={global.location}
              history={history}
              track={trackTitle}
              store={store}
              isLive
              analyticsMeta={analyticsMeta}
            />}
        </div>
        <LiveAccessSchedule
          buttonUrl={eventType !== 'AMA' && eventDetails.get('route') ? `${URL_GAIA_PLUS}/${eventDetails.get('route')}?default=2#event-information` : null}
          closeHandler={() => setLiveAccessScheduleVisible(false)}
          buttonText={eventType !== 'AMA' ? staticText.getIn(['data', 'seeSchedule']) : null}
          header={staticText.getIn(['data', 'scheduleHeader'])}
          visible={scheduleVisible}
          sessionList={schedule}
          language={language}
          nowTs={nowTs}
          full
        />
      </div>
      <LiveAccessCaptionsIframe
        isVisible={captionsVisible}
        url={captionsUrl}
      />
      <LiveExperienceControlBar
        showCaptions={!_isNil(captionsUrl)}
        trackTitles={trackTitles}
        setTrack={setTrack}
        track={track}
      />
    </div>
  )
}

LiveExperienceVideo.propTypes = {
  reloadEvent: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => {
      const language = state.user.getIn(['data', 'language', 0], 'en')
      const eventDetails = state.liveAccessEvents.getIn(['eventDetails',
        language, 'data', 'event'])
      const trackId = eventDetails ? eventDetails.getIn(['tracks', 0, 'id']) : 0
      const liveStreamActive = state.liveAccessEvents.getIn(['track', trackId, language, 'data', 'liveStreamActive'], undefined)
      return {
        eventDetails,
        user: state.user,
        auth: state.auth,
        language,
        sessionDetails: state.liveAccessEvents.get('sessionDetails'),
        poster: eventDetails.get('posterUrl'),
        debug: state.resolver.getIn(['query', 'debug']) === 'true',
        scheduleVisible: state.liveAccessEvents.getIn([scheduleKey, 'visible']),
        trackTitles: eventDetails ? eventDetails.get('tracks').map((e) => e.get('title')) : [],
        timenowOverride: state.resolver.getIn(['query', 'timenow']),
        liveStreamActive,
        captionsVisible: state.liveAccessEvents.getIn([captionsStoreKey, 'visible']),
        captionsUrl: eventDetails.get('captionsUrl'),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPageSeo: actions.page.setPageSeo,
        getLiveAccessSessionDetails: actions.liveAccessEvents.getLiveAccessSessionDetails,
        setLiveAccessScheduleVisible: actions.liveAccessEvents.setLiveAccessScheduleVisible,
        getLiveAccessTrack: actions.liveAccessEvents.getLiveAccessTrack,
      }
    },
  ),
  connectStaticText({ storeKey: 'livePage' }),
)(LiveExperienceVideo)
